import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'user-baseline-collection' | 'partner-baseline-collection' | 'relationship-snapshot' | 'relationship-context' | 'full-relationship-diagnostic';
export function RelationshipDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [hasUserBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasUserBaseline ? 'partner-baseline-collection' : 'user-baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // User baseline data
  const [userBaselineData, setUserBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Partner baseline data
  const [partnerBaselineData, setPartnerBaselineData] = useState({
    partnerName: '',
    dateOfBirth: '1992-06-20',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1992',
    month: '06',
    day: '20',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Relationship context data
  const [relationshipContext, setRelationshipContext] = useState({
    relationshipType: '',
    durationBand: '',
    tensionZones: [] as string[],
    contextNote: ''
  });
  // Relationship snapshot (baseline only)
  const relationshipSnapshot = {
    attractionMechanics: 'This bond formed through unconscious complementarity. One system stabilizes through internal structure and clarity; the other through external connection and emotional expression. The attraction is mutual — each system holds what the other lacks. The pull is not conscious choice but structural fit.',
    patternFit: 'These systems fit through stabilizing loops when both are regulated, and amplifying loops when pressure rises. One system over-functions cognitively; the other over-functions emotionally. This creates balance under low pressure but asymmetry under high pressure. Neither system is wrong — the fit is structural.',
    frictionZones: 'Tension is inevitable where one system needs space to process and the other needs connection to regulate. Where one system seeks clarity before action and the other seeks action to create clarity. Where one system withdraws under pressure and the other pursues. This is pattern collision, not personal failure.',
    currentBondPhase: 'This relationship is in the power negotiation phase. The initial bonding has stabilized. Now, each system is testing boundaries, control, and autonomy. Friction is higher than in the bonding phase but lower than in separation readiness. This phase determines whether the bond matures into integration or fragments.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    structuralInteraction: 'One system stabilizes through analysis and structure. The other through connection and emotional expression. Under low pressure, this creates complementarity. Under high pressure, it creates distance and pursuit.',
    currentExpression: 'After 3-7 years together, this interaction is expressing through communication friction and distance/closeness tension. The stabilizing loops that worked early in the bond are now creating friction. The system that withdraws is withdrawing more; the system that pursues is pursuing harder. The bond is testing whether it can recalibrate or whether the pattern will lock.',
    mutualTriggers: "One person triggers the other's fear of emotional overwhelm. The other triggers the first person's fear of abandonment. Neither person is doing this intentionally. The triggers are structural — each system's regulation pattern activates the other's core sensitivity. This is why the same conflict repeats even when intent is good.",
    growthBreakpointBoundary: 'This bond will mature if both systems can hold their own regulation without requiring the other to change. It will break if one system continues to require the other to regulate differently, or if the pursuit-withdrawal pattern locks into a stable loop. Growth is not about effort or love — it is about system capacity to hold tension without collapsing into blame.',
    whatChanged: {
      baseline: 'The baseline showed attraction mechanics (unconscious complementarity), pattern fit (stabilizing and amplifying loops), friction zones (space vs connection, clarity vs action), and current phase (power negotiation).',
      context: 'Adding relationship type (romantic), duration (3-7 years), and tension zones (communication, distance/closeness) revealed how the structural interaction is expressing in lived experience right now — and where the system is under the most strain.',
      whyConflictNotIncompatibility: 'Conflict is not incompatibility. Conflict is two systems trying to regulate in ways that collide. The baseline shows why the collision is inevitable. Context shows where it is happening right now. Neither person is the problem — the interaction pattern is.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateUserDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setUserBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setUserBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const updatePartnerDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setPartnerBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setPartnerBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateUserTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setUserBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const updatePartnerTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setPartnerBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleUserBaselineSubmit = () => {
    if (!userBaselineData.dateOfBirth || !userBaselineData.placeOfBirth || !userBaselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Saving your baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('partner-baseline-collection');
    }, 1500);
  };
  const handlePartnerBaselineSubmit = () => {
    if (!partnerBaselineData.partnerName || !partnerBaselineData.dateOfBirth || !partnerBaselineData.placeOfBirth || !partnerBaselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating relationship snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('relationship-snapshot');
    }, 2000);
  };
  const handleRelationshipContextSubmit = () => {
    setIsGenerating(true);
    setGeneratingMessage('Integrating context...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-relationship-diagnostic');
    }, 2000);
  };
  const handleToggleTensionZone = (zone: string) => {
    setRelationshipContext(prev => ({
      ...prev,
      tensionZones: prev.tensionZones.includes(zone) ? prev.tensionZones.filter(z => z !== zone) : [...prev.tensionZones, zone]
    }));
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-3558">
          <title data-id="element-3559">Generating Relationship Report | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-3560">
          <div className="text-center" data-id="element-3561">
            <div className="mb-6" data-id="element-3562">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-3563"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-3564">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-3565">
        <title data-id="element-3566">Relationship Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3567">
        <header data-id="element-3568">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3569">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3570">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3571">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3572" />
                <span className="text-lg font-medium tracking-tight" data-id="element-3573">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3574">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-3575">
          <div className="max-w-2xl mx-auto" data-id="element-3576">
            {/* Phases will be added in next edit due to length */}

            {/* PHASE 1A: User Baseline Collection - Abbreviated baseline UI */}
            {currentPhase === 'user-baseline-collection' && <div data-id="element-3577">
                <div className="mb-12" data-id="element-3578">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3579">
                    Relationship Decode
                  </h1>
                  <p className="text-lg text-[#F5F5F5]/60 italic mb-4" data-id="element-3580">
                    A diagnostic of how two systems interact
                  </p>
                  <p className="text-base text-[#F5F5F5]/70 leading-relaxed" data-id="element-3581">
                    This report maps attraction, friction, and growth dynamics —
                    without blame or prediction.
                  </p>
                </div>
                <div className="mb-8" data-id="element-3582">
                  <h2 className="text-xl font-medium mb-4" data-id="element-3583">Your baseline</h2>
                </div>
                <div className="space-y-8 mb-12" data-id="element-3584">
                  <div data-id="element-3585">
                    <label className="block text-sm font-medium mb-3" data-id="element-3586">
                      Date of birth
                    </label>
                    {userBaselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-3587">
                        <div className="text-2xl font-medium" data-id="element-3588">
                          {months.find(m => m.value === userBaselineData.month)?.label}{' '}
                          {userBaselineData.day}, {userBaselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-3589">
                      <div data-id="element-3590">
                        <label htmlFor="userMonth" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3591">
                          Month
                        </label>
                        <select id="userMonth" value={userBaselineData.month} onChange={e => updateUserDateOfBirth(userBaselineData.year, e.target.value, userBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3592">
                          <option value="" data-id="element-3593">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-3594">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3595">
                        <label htmlFor="userDay" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3596">
                          Day
                        </label>
                        <select id="userDay" value={userBaselineData.day} onChange={e => updateUserDateOfBirth(userBaselineData.year, userBaselineData.month, e.target.value)} disabled={!userBaselineData.month || !userBaselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-3597">
                          <option value="" data-id="element-3598">Day</option>
                          {Array.from({
                        length: getDaysInMonth(userBaselineData.year, userBaselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-3599">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3600">
                        <label htmlFor="userYear" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3601">
                          Year
                        </label>
                        <select id="userYear" value={userBaselineData.year} onChange={e => updateUserDateOfBirth(e.target.value, userBaselineData.month, userBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3602">
                          <option value="" data-id="element-3603">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-3604">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-3605">
                    <label htmlFor="userPlace" className="block text-sm font-medium mb-2" data-id="element-3606">
                      Place of birth
                    </label>
                    <input type="text" id="userPlace" value={userBaselineData.placeOfBirth} onChange={e => setUserBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3607" />
                  </div>
                  <div data-id="element-3608">
                    <label htmlFor="userCountry" className="block text-sm font-medium mb-2" data-id="element-3609">
                      Country
                    </label>
                    <input type="text" id="userCountry" value={userBaselineData.country} onChange={e => setUserBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3610" />
                  </div>
                </div>
                <div data-id="element-3611">
                  <label className="block text-sm font-medium mb-3" data-id="element-3612">
                    Time of birth{' '}
                    <span className="text-[#F5F5F5]/40" data-id="element-3613">(optional)</span>
                  </label>
                  <div className="space-y-3 mb-4" data-id="element-3614">
                    {[{
                  value: 'exact',
                  label: 'I know the exact time'
                }, {
                  value: 'approximate',
                  label: 'I know approximately'
                }, {
                  value: 'not-sure',
                  label: "Not sure / Don't know"
                }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${userBaselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3615">
                        <input type="radio" name="userTimeKnown" value={option.value} checked={userBaselineData.timeKnown === option.value} onChange={e => setUserBaselineData(prev => ({
                    ...prev,
                    timeKnown: e.target.value,
                    timeOfBirth: '',
                    hour: '12',
                    minute: '00',
                    period: 'AM'
                  }))} className="sr-only" data-id="element-3616" />
                        <span className="text-sm" data-id="element-3617">{option.label}</span>
                      </label>)}
                  </div>
                  {(userBaselineData.timeKnown === 'exact' || userBaselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-3618">
                      <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-3619">
                        <div className="text-center mb-8" data-id="element-3620">
                          <div className="text-4xl font-medium tracking-tight" data-id="element-3621">
                            {userBaselineData.hour}:{userBaselineData.minute}{' '}
                            {userBaselineData.period}
                          </div>
                          <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-3622">
                            {userBaselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                          </p>
                        </div>
                        <div className="mb-6" data-id="element-3623">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3624">
                            Hour
                          </label>
                          <div className="grid grid-cols-6 gap-2" data-id="element-3625">
                            {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateUserTimeOfBirth(h, userBaselineData.minute, userBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3626">
                                {h}
                              </button>)}
                          </div>
                        </div>
                        <div className="mb-6" data-id="element-3627">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3628">
                            Minute
                          </label>
                          <div className="grid grid-cols-6 gap-2" data-id="element-3629">
                            {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateUserTimeOfBirth(userBaselineData.hour, m, userBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3630">
                                {m}
                              </button>)}
                          </div>
                          <div className="mt-2" data-id="element-3631">
                            <input type="number" min="0" max="59" value={userBaselineData.minute} onChange={e => {
                        const val = e.target.value.padStart(2, '0');
                        if (parseInt(val) >= 0 && parseInt(val) <= 59) updateUserTimeOfBirth(userBaselineData.hour, val, userBaselineData.period);
                      }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3632" />
                          </div>
                        </div>
                        <div data-id="element-3633">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3634">
                            Period
                          </label>
                          <div className="grid grid-cols-2 gap-2" data-id="element-3635">
                            {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateUserTimeOfBirth(userBaselineData.hour, userBaselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3636">
                                {p}
                              </button>)}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>
                <button onClick={handleUserBaselineSubmit} disabled={!userBaselineData.dateOfBirth || !userBaselineData.placeOfBirth || !userBaselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3637">
                  Continue to Partner Baseline
                </button>
              </div>}

            {/* PHASE 1B: Partner Baseline Collection */}
            {currentPhase === 'partner-baseline-collection' && <div data-id="element-3638">
                <div className="mb-12" data-id="element-3639">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3640">
                    Partner Baseline
                  </h1>
                </div>
                <div className="space-y-8 mb-12" data-id="element-3641">
                  <div data-id="element-3642">
                    <label htmlFor="partnerName" className="block text-sm font-medium mb-2" data-id="element-3643">
                      Partner's name
                    </label>
                    <input type="text" id="partnerName" value={partnerBaselineData.partnerName} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  partnerName: e.target.value
                }))} placeholder="First name" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3644" />
                  </div>
                  <div data-id="element-3645">
                    <label className="block text-sm font-medium mb-3" data-id="element-3646">
                      Date of birth
                    </label>
                    {partnerBaselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-3647">
                        <div className="text-2xl font-medium" data-id="element-3648">
                          {months.find(m => m.value === partnerBaselineData.month)?.label}{' '}
                          {partnerBaselineData.day}, {partnerBaselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-3649">
                      <div data-id="element-3650">
                        <label htmlFor="partnerMonth" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3651">
                          Month
                        </label>
                        <select id="partnerMonth" value={partnerBaselineData.month} onChange={e => updatePartnerDateOfBirth(partnerBaselineData.year, e.target.value, partnerBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3652">
                          <option value="" data-id="element-3653">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-3654">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3655">
                        <label htmlFor="partnerDay" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3656">
                          Day
                        </label>
                        <select id="partnerDay" value={partnerBaselineData.day} onChange={e => updatePartnerDateOfBirth(partnerBaselineData.year, partnerBaselineData.month, e.target.value)} disabled={!partnerBaselineData.month || !partnerBaselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-3657">
                          <option value="" data-id="element-3658">Day</option>
                          {Array.from({
                        length: getDaysInMonth(partnerBaselineData.year, partnerBaselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-3659">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3660">
                        <label htmlFor="partnerYear" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3661">
                          Year
                        </label>
                        <select id="partnerYear" value={partnerBaselineData.year} onChange={e => updatePartnerDateOfBirth(e.target.value, partnerBaselineData.month, partnerBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3662">
                          <option value="" data-id="element-3663">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-3664">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-3665">
                    <label htmlFor="partnerPlace" className="block text-sm font-medium mb-2" data-id="element-3666">
                      Place of birth
                    </label>
                    <input type="text" id="partnerPlace" value={partnerBaselineData.placeOfBirth} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3667" />
                  </div>
                  <div data-id="element-3668">
                    <label htmlFor="partnerCountry" className="block text-sm font-medium mb-2" data-id="element-3669">
                      Country
                    </label>
                    <input type="text" id="partnerCountry" value={partnerBaselineData.country} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3670" />
                  </div>
                  <div data-id="element-3671">
                    <label className="block text-sm font-medium mb-3" data-id="element-3672">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-3673">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-3674">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${partnerBaselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3675">
                          <input type="radio" name="partnerTimeKnown" value={option.value} checked={partnerBaselineData.timeKnown === option.value} onChange={e => setPartnerBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-3676" />
                          <span className="text-sm" data-id="element-3677">{option.label}</span>
                        </label>)}
                    </div>
                    {(partnerBaselineData.timeKnown === 'exact' || partnerBaselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-3678">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-3679">
                          <div className="text-center mb-8" data-id="element-3680">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-3681">
                              {partnerBaselineData.hour}:
                              {partnerBaselineData.minute}{' '}
                              {partnerBaselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-3682">
                              {partnerBaselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-3683">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3684">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-3685">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updatePartnerTimeOfBirth(h, partnerBaselineData.minute, partnerBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3686">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-3687">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3688">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-3689">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updatePartnerTimeOfBirth(partnerBaselineData.hour, m, partnerBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3690">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-3691">
                              <input type="number" min="0" max="59" value={partnerBaselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updatePartnerTimeOfBirth(partnerBaselineData.hour, val, partnerBaselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3692" />
                            </div>
                          </div>
                          <div data-id="element-3693">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3694">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-3695">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updatePartnerTimeOfBirth(partnerBaselineData.hour, partnerBaselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3696">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                <button onClick={handlePartnerBaselineSubmit} disabled={!partnerBaselineData.partnerName || !partnerBaselineData.dateOfBirth || !partnerBaselineData.placeOfBirth || !partnerBaselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3697">
                  Generate Relationship Snapshot
                </button>
              </div>}

            {/* PHASE 2: Relationship Snapshot */}
            {currentPhase === 'relationship-snapshot' && <div data-id="element-3698">
                <div className="mb-12" data-id="element-3699">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3700">
                    Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3701">
                    Structural interaction
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3702">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3703">
                    <span className="text-2xl" data-id="element-3704">🧲</span>
                    <div className="flex-1" data-id="element-3705">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3706">
                        Why this bond formed
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3707">
                        {relationshipSnapshot.attractionMechanics}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3708">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3709">
                    <span className="text-2xl" data-id="element-3710">🧬</span>
                    <div className="flex-1" data-id="element-3711">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3712">
                        How the systems fit
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3713">
                        {relationshipSnapshot.patternFit}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3714">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3715">
                    <span className="text-2xl" data-id="element-3716">⚠️</span>
                    <div className="flex-1" data-id="element-3717">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3718">
                        Where tension is inevitable
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3719">
                        {relationshipSnapshot.frictionZones}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3720">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3721">
                    <span className="text-2xl" data-id="element-3722">⏳</span>
                    <div className="flex-1" data-id="element-3723">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3724">
                        What phase this relationship is in
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3725">
                        {relationshipSnapshot.currentBondPhase}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-3726">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-3727">
                    Structural relationship baseline — independent of effort or
                    intent
                  </p>
                </div>
                <div className="mb-12" data-id="element-3728">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-3729">
                    <span data-id="element-3730">📘</span>
                    <span data-id="element-3731">How this relationship snapshot is generated</span>
                    <span className="text-xs" data-id="element-3732">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3733">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-3734">
                        This snapshot is generated by analyzing two independent
                        structural baselines and how they interact.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-3735">
                        It does not analyze behavior, communication style, or
                        effort.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3736">
                        The next questions do not change the structure of the
                        bond. They localize where friction is currently being
                        experienced.
                      </p>
                    </div>}
                </div>
                <div className="space-y-4" data-id="element-3737">
                  <button onClick={() => setCurrentPhase('relationship-context')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3738">
                    🧩 Add relationship context (optional)
                  </button>
                  <div className="text-center" data-id="element-3739">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-3740">
                      Optional · 4 quick questions
                    </p>
                    <p className="text-xs text-[#F5F5F5]/40 leading-relaxed max-w-md mx-auto" data-id="element-3741">
                      This localizes where friction is showing up right now.
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3742">
                    Save Report
                  </button>
                </div>
              </div>}

            {/* PHASE 3: Relationship Context */}
            {currentPhase === 'relationship-context' && <div data-id="element-3743">
                <div className="mb-12" data-id="element-3744">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3745">
                    Add context
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3746">
                    Optional · 4 quick questions
                  </p>
                </div>
                <div className="space-y-12 mb-12" data-id="element-3747">
                  <div data-id="element-3748">
                    <h2 className="text-lg font-medium mb-6" data-id="element-3749">
                      Relationship type
                    </h2>
                    <div className="space-y-3" data-id="element-3750">
                      {['Romantic', 'Marriage', 'Parent–child', 'Siblings', 'Professional partnership'].map(type => <label key={type} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.relationshipType === type ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3751">
                          <input type="radio" name="relationshipType" value={type} checked={relationshipContext.relationshipType === type} onChange={e => setRelationshipContext(prev => ({
                      ...prev,
                      relationshipType: e.target.value
                    }))} className="sr-only" data-id="element-3752" />
                          <span className="text-base" data-id="element-3753">{type}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-3754">
                    <h2 className="text-lg font-medium mb-6" data-id="element-3755">Duration</h2>
                    <div className="space-y-3" data-id="element-3756">
                      {['Less than 1 year', '1–3 years', '3–7 years', '7+ years'].map(duration => <label key={duration} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.durationBand === duration ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3757">
                          <input type="radio" name="durationBand" value={duration} checked={relationshipContext.durationBand === duration} onChange={e => setRelationshipContext(prev => ({
                      ...prev,
                      durationBand: e.target.value
                    }))} className="sr-only" data-id="element-3758" />
                          <span className="text-base" data-id="element-3759">{duration}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-3760">
                    <h2 className="text-lg font-medium mb-3" data-id="element-3761">
                      Where does tension show up most right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-3762">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-3763">
                      {['Communication', 'Control / freedom', 'Emotional safety', 'Distance / closeness', 'Responsibility imbalance'].map(zone => <label key={zone} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.tensionZones.includes(zone) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3764">
                          <input type="checkbox" value={zone} checked={relationshipContext.tensionZones.includes(zone)} onChange={() => handleToggleTensionZone(zone)} className="sr-only" data-id="element-3765" />
                          <span className="text-base" data-id="element-3766">{zone}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-3767">
                    <h2 className="text-lg font-medium mb-3" data-id="element-3768">
                      Anything important about this bond right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-3769">Optional</p>
                    <textarea value={relationshipContext.contextNote} onChange={e => setRelationshipContext(prev => ({
                  ...prev,
                  contextNote: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional..." data-id="element-3770" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-3771">
                      {relationshipContext.contextNote.length}/200
                    </p>
                  </div>
                </div>
                <button onClick={handleRelationshipContextSubmit} disabled={!relationshipContext.relationshipType || !relationshipContext.durationBand} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3772">
                  Apply Context
                </button>
              </div>}

            {/* PHASE 4: Full Relationship Diagnostic */}
            {currentPhase === 'full-relationship-diagnostic' && <div data-id="element-3773">
                <div className="mb-12" data-id="element-3774">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3775">
                    Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3776">Full Diagnostic</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3777">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3778">
                    <span className="text-2xl" data-id="element-3779">🧱</span>
                    <div className="flex-1" data-id="element-3780">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-3781">
                        How these systems interact by default
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-3782">
                        {fullDiagnosticOutput.structuralInteraction}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3783">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3784">
                    <span className="text-2xl" data-id="element-3785">🔄</span>
                    <div className="flex-1" data-id="element-3786">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-3787">
                        How this interaction is expressing right now
                      </p>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3788">
                        {fullDiagnosticOutput.currentExpression}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3789">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3790">
                    <span className="text-2xl" data-id="element-3791">🪞</span>
                    <div className="flex-1" data-id="element-3792">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3793">
                        What each person triggers in the other
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3794">
                        {fullDiagnosticOutput.mutualTriggers}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3795">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3796">
                    <span className="text-2xl" data-id="element-3797">⚠️</span>
                    <div className="flex-1" data-id="element-3798">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3799">
                        What determines whether this bond matures or breaks
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3800">
                        {fullDiagnosticOutput.growthBreakpointBoundary}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12" data-id="element-3801">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-3802">
                    <span data-id="element-3803">🔍</span>
                    <span data-id="element-3804">What changed after adding context</span>
                    <span className="text-xs" data-id="element-3805">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-3806">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3807">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3808">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3809">
                          {fullDiagnosticOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3810">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3811">
                          What context revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3812">
                          {fullDiagnosticOutput.whatChanged.context}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3813">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3814">
                          Why conflict ≠ incompatibility
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3815">
                          {fullDiagnosticOutput.whatChanged.whyConflictNotIncompatibility}
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3816">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3817">
                    <span className="text-2xl" data-id="element-3818">🔁</span>
                    <div className="flex-1" data-id="element-3819">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3820">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-3821">
                        This interaction pattern is structural, not situational.
                        The same dynamics appear across time and circumstances
                        because they emerge from how these two systems are
                        wired, not from what either person is doing wrong.
                      </p>
                      <div className="space-y-4" data-id="element-3822">
                        <div data-id="element-3823">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-3824">
                            Similar dynamics in other close bonds
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-3825">
                            If you examine other significant relationships, you
                            will likely find similar patterns: one system seeks
                            space to process, the other seeks connection to
                            regulate. One system withdraws under pressure, the
                            other pursues. The specific people change, but the
                            structural interaction repeats.
                          </p>
                        </div>
                        <div data-id="element-3826">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-3827">
                            Repetition across time
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-3828">
                            The same friction points recur across the lifespan
                            of this relationship. Early bonding may have masked
                            them, but as pressure increases or novelty fades,
                            the structural patterns become more visible. The
                            repetition is not failure — it is the pattern
                            expressing under different conditions.
                          </p>
                        </div>
                        <div data-id="element-3829">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-3830">
                            Same friction despite different circumstances
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-3831">
                            Changing external circumstances — moving, changing
                            jobs, having children — does not resolve the core
                            friction. The circumstances shift, but the way these
                            two systems interact under pressure remains
                            structurally consistent. This is not about effort or
                            intent — it is about pattern collision.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3832">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3833">
                    <span className="text-2xl" data-id="element-3834">⚖️</span>
                    <div className="flex-1" data-id="element-3835">
                      <h2 className="text-xl font-medium mb-6" data-id="element-3836">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-3837">
                        <div data-id="element-3838">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-3839">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-3840">
                            <li data-id="element-3841">
                              • Why attraction and friction coexist in this bond
                            </li>
                            <li data-id="element-3842">
                              • Why effort alone doesn't resolve structural
                              tension
                            </li>
                            <li data-id="element-3843">
                              • Why the same themes recur across time and
                              circumstances
                            </li>
                            <li data-id="element-3844">
                              • How two systems interact under their structural
                              constraints
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-3845">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-3846">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-3847">
                            <li data-id="element-3848">
                              • Relationship outcomes or whether this bond will
                              last
                            </li>
                            <li data-id="element-3849">
                              • Who is right or wrong in specific conflicts
                            </li>
                            <li data-id="element-3850">
                              • What should be done next or how to fix the
                              dynamic
                            </li>
                            <li data-id="element-3851">
                              • Daily moods, temporary phases, or situational
                              reactions
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3852">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3853">
                    <span className="text-2xl" data-id="element-3854">🧭</span>
                    <div className="flex-1" data-id="element-3855">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3856">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3857">
                        These are two systems that attracted through
                        complementarity and now experience friction through the
                        same structural differences. One system stabilizes
                        through internal processing and space; the other through
                        connection and expression. Under low pressure, this
                        creates balance; under high pressure, it creates
                        distance and pursuit. The pattern repeats because each
                        system is trying to regulate in ways that structurally
                        collide, not because either person is failing. The bond
                        will mature if both systems can hold their own
                        regulation without requiring the other to change.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-3858">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3859">
                    💾 Save Relationship Decode
                  </button>
                  <button onClick={() => {
                setCurrentPhase('partner-baseline-collection');
                setPartnerBaselineData({
                  partnerName: '',
                  dateOfBirth: '',
                  timeOfBirth: '',
                  timeKnown: 'not-sure',
                  placeOfBirth: '',
                  country: '',
                  year: '',
                  month: '',
                  day: '',
                  hour: '12',
                  minute: '00',
                  period: 'AM'
                });
                setRelationshipContext({
                  relationshipType: '',
                  durationBand: '',
                  tensionZones: [],
                  contextNote: ''
                });
              }} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3860">
                    ➕ Add another relationship
                  </button>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}